import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
} from '@cartcloser/ui';
import {
  createWidgetKey,
  getWidgetBranding,
  listWidgetKeys,
  revokeWidgetKey,
  saveWidgetBranding,
} from '../lib/api.js';

export const Route = createFileRoute('/admin/$slug/settings')({
  component: SettingsPage,
});

// In production the widget Worker is served from its own host.
const WIDGET_ORIGIN = 'https://widget.cartcloser.com';

function SettingsPage() {
  const { slug } = Route.useParams();
  const queryClient = useQueryClient();
  const keys = useQuery({ queryKey: ['widget-keys', slug], queryFn: () => listWidgetKeys(slug) });
  const [origins, setOrigins] = useState('');

  const create = useMutation({
    mutationFn: () =>
      createWidgetKey(
        slug,
        origins
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      ),
    onSuccess: async () => {
      setOrigins('');
      await queryClient.invalidateQueries({ queryKey: ['widget-keys', slug] });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeWidgetKey(slug, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['widget-keys', slug] }),
  });

  const activeKey = keys.data?.find((k) => !k.revoked);
  const snippet = activeKey
    ? `<script src="${WIDGET_ORIGIN}/loader.js" data-key="${activeKey.publishableKey}" async></script>`
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your storefront widget.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Widget keys</CardTitle>
          <CardDescription>
            Create a publishable key and paste the snippet into your storefront. The key only
            authorises chat from your allowed origins.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <FormField
              label="Allowed origins"
              description="Comma-separated, e.g. https://shop.example.com"
              className="flex-1"
            >
              {({ id }) => (
                <Input
                  id={id}
                  value={origins}
                  onChange={(e) => setOrigins(e.target.value)}
                  placeholder="https://shop.example.com"
                />
              )}
            </FormField>
            <Button type="submit" loading={create.isPending}>
              Create key
            </Button>
          </form>

          {snippet ? (
            <div className="space-y-1">
              <p className="text-sm font-medium">Embed snippet</p>
              <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs">{snippet}</pre>
            </div>
          ) : null}

          <div className="space-y-2">
            {keys.data?.map((k) => (
              <div
                key={k.id}
                className="flex items-center justify-between gap-3 rounded-md border border-border p-3 text-sm"
              >
                <div className="min-w-0">
                  <span className="break-all font-mono text-xs">{k.publishableKey}</span>
                  {k.allowedOrigins.length ? (
                    <p className="break-words text-xs text-muted-foreground">
                      {k.allowedOrigins.join(', ')}
                    </p>
                  ) : (
                    <p className="text-xs text-amber-600">
                      Any origin — anyone can embed this key. Add your storefront origin to lock it
                      down.
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {k.revoked ? (
                    <Badge variant="secondary">Revoked</Badge>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => revoke.mutate(k.id)}>
                      Revoke
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {keys.data?.length === 0 ? (
              <p className="text-sm text-muted-foreground">No keys yet.</p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <BrandingCard slug={slug} />
    </div>
  );
}

function BrandingCard({ slug }: { slug: string }) {
  const queryClient = useQueryClient();
  const branding = useQuery({
    queryKey: ['widget-branding', slug],
    queryFn: () => getWidgetBranding(slug),
  });

  const [greeting, setGreeting] = useState<string | null>(null);
  const [assistantName, setAssistantName] = useState<string | null>(null);
  const [accentColor, setAccentColor] = useState<string | null>(null);

  // Seed local state from the loaded branding once.
  const data = branding.data;
  const g = greeting ?? data?.greeting ?? '';
  const n = assistantName ?? data?.assistantName ?? '';
  const c = accentColor ?? data?.accentColor ?? '#1a7d4b';

  const save = useMutation({
    mutationFn: () =>
      saveWidgetBranding(slug, { greeting: g, assistantName: n, accentColor: c }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['widget-branding', slug] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Widget branding</CardTitle>
        <CardDescription>
          Customise how the assistant introduces itself on your storefront. Leave a field blank to
          use the default.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {branding.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <FormField label="Assistant name" description="Shown in the widget header.">
              {({ id }) => (
                <Input
                  id={id}
                  value={n}
                  maxLength={60}
                  onChange={(e) => setAssistantName(e.target.value)}
                  placeholder="Ask our product expert"
                />
              )}
            </FormField>
            <FormField label="Greeting" description="The assistant's first message.">
              {({ id }) => (
                <Input
                  id={id}
                  value={g}
                  maxLength={200}
                  onChange={(e) => setGreeting(e.target.value)}
                  placeholder="Hi! What are you shopping for today?"
                />
              )}
            </FormField>
            <FormField label="Accent colour" description="Hex, e.g. #1a7d4b.">
              {({ id }) => (
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(c) ? c : '#1a7d4b'}
                    onChange={(e) => setAccentColor(e.target.value)}
                    className="h-9 w-12 cursor-pointer rounded border border-border"
                    aria-label="Accent colour"
                  />
                  <Input
                    id={id}
                    value={c}
                    onChange={(e) => setAccentColor(e.target.value)}
                    placeholder="#1a7d4b"
                    className="max-w-[160px] font-mono"
                  />
                </div>
              )}
            </FormField>
            <div className="flex items-center gap-3">
              <Button type="submit" loading={save.isPending}>
                Save branding
              </Button>
              {save.isError ? (
                <span className="text-sm text-destructive">Couldn’t save. Check the accent colour.</span>
              ) : null}
              {save.isSuccess ? <span className="text-sm text-primary">Saved.</span> : null}
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
